'use client'

import { usePlethoreForm } from '@/contexts/FormContext'
import { useT } from '@/i18n/client'
import { uploadMembershipProfilePicture } from '@/server/actions/membershipProfile'
import { createSubscription } from '@/server/actions/subscription'
import { SBIcon } from '@supplement-bacon/react-toolkit/components'
import {
  Alert,
  App,
  Button,
  Flex,
  Form,
  Space,
  Spin,
  Steps,
  UploadFile,
} from 'antd'
import { isAxiosError } from 'axios'
import { useEffect, useState } from 'react'
import Resizer from 'react-image-file-resizer'
import { DevAutoFill } from './DevAutoFill'
import SBDynamicForm from './SBDynamicForm'

const FormWrapper = () => {
  const { t } = useT('form')
  const [form] = Form.useForm()
  const { message } = App.useApp()
  const { steps, currentStep, setCurrentStep } = usePlethoreForm()

  const [isMobile, setIsMobile] = useState(false)
  const [errorsCount, setErrorsCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleNext = async () => {
    if (steps === undefined) return
    try {
      setIsLoading(true)
      await form.validateFields([steps[currentStep].pathPrefix], {
        recursive: true,
      })
      setErrorsCount(0)
      const finalStepIndex = steps.length
      const nextCurrent = Math.min(currentStep + 1, finalStepIndex)

      if (nextCurrent === finalStepIndex) {
        form.submit()
      } else {
        setCurrentStep(nextCurrent)
        setIsLoading(false)
        window.scrollTo(0, 0)
      }
    } catch (error: any) {
      setIsLoading(false)
      setErrorsCount(error.errorFields.length)
    }
  }

  const handlePrev = () => {
    setErrorsCount(0)
    setCurrentStep((prev) => Math.max(prev - 1, 0))
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMobile(window.innerWidth < 748)
    }
  }, [])

  if (!steps) {
    return <Spin />
  }

  return (
    <Flex
      style={{ maxWidth: 800, margin: 'auto', width: '100%', paddingTop: 50 }}
      vertical
      gap={50}
    >
      <Steps
        type={isMobile ? 'inline' : 'default'}
        style={{ margin: 'auto' }}
        current={currentStep}
        items={[
          ...steps.map((formStep) => ({
            title: formStep.title,
            icon: <SBIcon name={formStep.icon} />,
          })),
          // We make the illusion that the payment is a step, but it's actually the subscription details page
          {
            title: t('steps.payment'),
            icon: <SBIcon name="CreditCardOutlined" />,
          },
        ]}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={async () => {
          try {
            const photoFields: UploadFile[] = []

            const subscriptionResult = await createSubscription(
              steps.map((step) => {
                const stepFields = form.getFieldValue(step.pathPrefix)
                // Remove photo fields before sending to server as it's handled separately
                if ('photo' in stepFields) {
                  // The stepFields['photo'] is an array of UploadFile
                  photoFields.push(stepFields['photo'][0])
                  delete stepFields['photo']
                }
                return {
                  type: step.type.name,
                  items: stepFields,
                }
              })
            )

            await Promise.all(
              subscriptionResult.membershipProfiles.map(
                async (profileId, index) => {
                  const picture = photoFields[index]
                  if (!picture || !picture.originFileObj) {
                    return Promise.reject(
                      `No picture to upload for index ${index}`
                    )
                  }
                  const resizedPicture: File = await new Promise(
                    (resolve, reject) => {
                      Resizer.imageFileResizer(
                        picture.originFileObj!,
                        1024, // maxWidth
                        1024, // maxHeight
                        'JPEG', // compressFormat
                        50, // quality
                        0, // rotation
                        (uri) => {
                          if (uri instanceof Blob) {
                            resolve(
                              new File([uri], picture.name, {
                                type: 'image/jpeg',
                              })
                            )
                          } else {
                            // fallback if uri is not a Blob
                            reject(new Error('Image resizing failed'))
                          }
                        },
                        'blob'
                      )
                    }
                  )

                  const formData = new FormData()
                  formData.append('profileId', String(profileId))
                  formData.append('picture', resizedPicture)

                  await uploadMembershipProfilePicture(formData)
                }
              )
            )

            window.location.href = `/subscriptions/${subscriptionResult.uuid}`
          } catch (error: any) {
            setIsLoading(false)

            if (
              error.message &&
              error.message.includes('unexpected response')
            ) {
              message.error(t('subscriptionFailedDueToServer'))
            } else if (isAxiosError(error)) {
              message.error(error.message)
            } else {
              message.error(error.message || t('unknownError'))
            }
          }
        }}
      >
        <DevAutoFill />
        {steps.map((step, index) => (
          <SBDynamicForm.Step
            key={index}
            step={step}
            flexStyle={{ display: currentStep === index ? 'block' : 'none' }}
          />
        ))}
      </Form>

      {errorsCount > 0 && (
        <Alert type="error" title={`${errorsCount} ${t('errorsFound')}`} />
      )}

      <Space style={{ justifyContent: 'flex-end' }}>
        {currentStep > 0 && (
          <Button loading={isLoading} onClick={handlePrev}>
            {t('navigation.previous')}
          </Button>
        )}
        <Button loading={isLoading} onClick={handleNext} type="primary">
          {t('navigation.next')}
        </Button>
      </Space>
    </Flex>
  )
}

export default FormWrapper
